import { Drawer } from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import { useCreateFundBusiness } from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import type { FundBusinessCreateFormSchema } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { getInitialPerformancesData } from '@/features/project/projects/detail/fund-bussiness/_utils/fund-business';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { CreateFundBusinessForm } from './CreateFundBusinessForm';

export const CreateFundBusinessFormDrawer: FC = () => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { mutateAsync: createFundBusiness } = useCreateFundBusiness(projectId);

  const isDrawerOpen =
    drawerState.open &&
    drawerState.type === 'fund-business' &&
    project.data?.fundAdditionalAttributes?.fundSheetType === 'standard' &&
    !drawerState.isEditing;

  const closeDrawer = () => {
    setDrawerState({ open: false });
  };

  const handleSubmit = async (
    values: z.infer<typeof FundBusinessCreateFormSchema>,
  ) => {
    try {
      await createFundBusiness({
        title: values.title,
        businessForm: values.businessForm,
        performances: getInitialPerformancesData(values.businessForm),
      });
      await Promise.all([
        openMessage('success', t('message-create-completed')),
        (async () => {
          closeDrawer();
        })(),
      ]);
    } catch {
      await openMessage('error', t('message-create-failed'));
    }
  };

  return (
    <Drawer destroyOnClose open={isDrawerOpen} onClose={closeDrawer}>
      <CreateFundBusinessForm
        initialValues={
          isDrawerOpen
            ? {
                businessForm: drawerState.businessForm,
              }
            : undefined
        }
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
};
